/**
 * Runner Sidebar — slide-in panel for playback & planning screens.
 * Shows runner details with live-updating position & ETA.
 */

import type { RunnerDTO, PlanEntryDTO, RipperdocDTO } from '../data/fixture';
import type { MovementRecord } from '../data/timeline';
import { t } from '../i18n';
import { portraitHTML, hydratePortraits } from '../scene/PortraitEngine';
import { hpColor, heatColor, loyColor } from '../utils/colors';
import { statusBadge, skillChips, inlineAttrBar as attrBar, inlineStateBar as stateBar } from '../utils/ui';
import {
  renderCyberwareUI, wireCyberwareUI, injectCyberwareStyles,
  type CyberwareUIOptions,
} from './cyberware-section';

// ── Types ────────────────────────────────────────────────────────────────────

export interface RunnerSidebarData {
  runner: RunnerDTO;
  /** Current movement (playback mode only). */
  movement?: MovementRecord | null;
  /** Current tick (playback mode). */
  tick?: number;
  /** Plan entries assigned to this runner (planning mode). */
  planEntries?: PlanEntryDTO[];
  /** Called when edit button is clicked in planning mode. */
  onEditPlan?: (runnerName: string) => void;
  /** Phase 4 (#589) — ripperdocs the player has discovered. Drives the
   *  inline INSTALL_CYBERWARE form. Empty / undefined → form replaced
   *  by an "scout sectors to find ripperdocs" empty state. */
  ripperdocs?: RipperdocDTO[];
  /** Phase 4 (#589) — current clean cash. Used by the cost-preview
   *  affordability indicator. Undefined leaves affordability unchecked
   *  (the validator still rejects on submit). */
  cleanCash?: number;
  /** Phase 4 (#589) — submit handler for INSTALL_CYBERWARE. Resolves
   *  to true when the plan submit succeeds, false otherwise. Caller is
   *  expected to refresh the sidebar with the new fixture afterwards
   *  so the install reflects on the runner. */
  onInstallCyberware?: (
    runnerId: string,
    ripperdocNodeId: string,
    cyberwareId: string,
    targetPart: string,
  ) => Promise<boolean>;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtMin(t: number): string {
  const h = Math.floor(t / 60).toString().padStart(2, '0');
  const m = (t % 60).toString().padStart(2, '0');
  return `${h}:${m}`;
}

function resolveNodeSector(nodeId: string): string {
  const m = nodeId.match(/^(S-\d+)/);
  return m ? m[1] : nodeId;
}

// ── Component ────────────────────────────────────────────────────────────────

export class RunnerSidebar {
  readonly el: HTMLElement;
  data: RunnerSidebarData | null = null;
  private onClose: (() => void) | null = null;
  private escHandler: ((e: KeyboardEvent) => void) | null = null;
  private outsideHandler: ((e: MouseEvent) => void) | null = null;

  constructor() {
    this.el = document.createElement('div');
    this.el.className = 'runner-sidebar';
    this.el.id = 'runner-sidebar';
    this.injectStyles();
    injectCyberwareStyles();
  }

  private injectStyles(): void {
    if (document.querySelector('#runner-sidebar-style')) return;
    const s = document.createElement('style');
    s.id = 'runner-sidebar-style';
    s.textContent = `
      .runner-sidebar {
        position:absolute; top:0; right:0; bottom:0; width:280px;
        background:var(--bg-panel,#0e1118); border-left:1px solid var(--border);
        transform:translateX(100%); transition:transform .2s ease;
        overflow-y:auto; z-index:20; padding:14px;
        display:flex; flex-direction:column; gap:10px;
      }
      .runner-sidebar.open { transform:translateX(0); }
      .runner-sidebar .rs-header { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
      .runner-sidebar .rs-name { font-weight:700; font-size:15px; color:var(--text-hi); }
      .runner-sidebar .rs-role { font-size:11px; color:var(--text-lo); }
      .runner-sidebar .rs-close {
        background:none; border:1px solid var(--border); color:var(--text-lo);
        font-size:13px; width:22px; height:22px; border-radius:3px; cursor:pointer;
        margin-left:auto; line-height:1;
      }
      .runner-sidebar .rs-close:hover { color:var(--red); border-color:var(--red); }
      .runner-sidebar .rs-section { background:var(--bg); border:1px solid var(--border); border-radius:3px; padding:10px; }
      .runner-sidebar .rs-section-title { font-weight:700; font-size:12px; color:var(--text-hi); margin-bottom:8px; }
      .runner-sidebar .rs-route { font-family:var(--mono); font-size:11px; color:var(--text-mid); }
      .runner-sidebar .rs-edit-btn {
        background:rgba(var(--ac),0.08); border:1px solid var(--cyan-dim,var(--border));
        color:var(--cyan); font-family:var(--mono); font-size:11px; padding:4px 10px;
        border-radius:3px; cursor:pointer; margin-top:6px;
      }
      .runner-sidebar .rs-edit-btn:hover { border-color:var(--cyan); }

      /* Phase 3 body grid + Phase 4 cyberware styles live in
         cyberware-section.ts (cw-* classes) and are injected by
         injectCyberwareStyles() — shared between the sidebar and
         runners.ts. */

      @media (max-width: 768px) {
        .runner-sidebar {
          position:fixed; top:auto; right:0; bottom:0; left:0;
          width:100%; max-height:60vh; border-left:none;
          border-top:1px solid var(--border); border-radius:12px 12px 0 0;
          transform:translateY(100%);
        }
        .runner-sidebar.open { transform:translateY(0); }
      }
    `;
    document.head.appendChild(s);
  }

  open(data: RunnerSidebarData, onClose?: () => void): void {
    this.data = data;
    this.onClose = onClose ?? null;
    this.render();
    // Small delay to trigger CSS transition
    requestAnimationFrame(() => this.el.classList.add('open'));

    // Close handlers
    this.escHandler = (e: KeyboardEvent) => { if (e.key === 'Escape') this.close(); };
    document.addEventListener('keydown', this.escHandler);

    this.outsideHandler = (e: MouseEvent) => {
      if (!this.el.contains(e.target as Node) && this.el.classList.contains('open')) {
        this.close();
      }
    };
    // Delay attaching click-outside to avoid immediately closing from the opening click
    setTimeout(() => document.addEventListener('click', this.outsideHandler!), 100);
  }

  close(): void {
    this.el.classList.remove('open');
    if (this.escHandler) { document.removeEventListener('keydown', this.escHandler); this.escHandler = null; }
    if (this.outsideHandler) { document.removeEventListener('click', this.outsideHandler); this.outsideHandler = null; }
    if (this.onClose) this.onClose();
  }

  get isOpen(): boolean { return this.el.classList.contains('open'); }

  /** Update live data (position, movement, tick) without re-opening. */
  update(data: Partial<RunnerSidebarData>): void {
    if (!this.data) return;
    Object.assign(this.data, data);
    this.renderRouteSection();
  }

  private render(): void {
    const d = this.data;
    if (!d) return;
    const r = d.runner;

    this.el.innerHTML = `
      <div class="rs-header">
        <span class="rs-name">${r.name}</span>
        <span class="rs-role">${skillChips(r.skills)}</span>
        ${statusBadge(r)}
        <button class="rs-close">✕</button>
      </div>

      <div style="text-align:center;margin:4px 0" id="rs-portrait-container">
        ${portraitHTML(r, 90)}
      </div>

      <div class="rs-section">
        <div class="rs-section-title">${t('sidebar.attributes')}</div>
        ${attrBar('STR', r.str)}
        ${attrBar('INT', r.int)}
        ${attrBar('STL', r.stl)}
        ${attrBar('TCH', r.tch)}
        ${attrBar('CHA', r.cha)}
        ${attrBar('CON', r.con)}
      </div>

      <div class="rs-section">
        <div class="rs-section-title">${t('sidebar.status')}</div>
        ${stateBar('HP', r.hp, hpColor(r.hp), r.hp < 30)}
        ${stateBar('LOY', r.loy, loyColor(r.loy), r.loy < 40)}
        ${stateBar('HEAT', r.heat, heatColor(r.heat), r.heat > 60)}
        ${stateBar('STRS', r.stress, r.stress > 70 ? 'var(--red)' : 'var(--text-lo)', r.stress > 70)}
      </div>

      ${renderCyberwareUI(this.cyberwareOpts())}

      <div class="rs-section" id="rs-route-section"></div>

      ${d.planEntries && d.planEntries.length > 0 ? `
        <div class="rs-section">
          <div class="rs-section-title">${t('sidebar.plan')}</div>
          ${d.planEntries.map(e => `<div class="rs-route">${e.action} → ${e.sector || '—'}</div>`).join('')}
          <button class="rs-edit-btn" id="rs-edit-plan">${t('sidebar.edit_plan')}</button>
        </div>
      ` : ''}
    `;

    this.renderRouteSection();

    // Render portrait
    hydratePortraits(this.el);

    // Wire close button
    this.el.querySelector('.rs-close')?.addEventListener('click', () => this.close());

    // Wire edit button
    this.el.querySelector('#rs-edit-plan')?.addEventListener('click', () => {
      if (this.data?.onEditPlan && this.data.runner) {
        this.data.onEditPlan(this.data.runner.name);
      }
    });

    // Wire shared Phase 4 cyberware controls (toggle + selects + submit).
    wireCyberwareUI(this.el, this.cyberwareOpts());
  }

  private renderRouteSection(): void {
    const section = this.el.querySelector('#rs-route-section');
    if (!section || !this.data) return;

    const d = this.data;
    const mv = d.movement;

    if (mv && d.tick != null) {
      const from = resolveNodeSector(mv.from_node);
      const to = resolveNodeSector(mv.to_node);
      const eta = mv.tick_end;
      const remaining = Math.max(0, eta - d.tick);
      section.innerHTML = `
        <div class="rs-section-title">${t('sidebar.route')}</div>
        <div class="rs-route">${t('sidebar.status_label')}: ${t('sidebar.on_route')}</div>
        <div class="rs-route">${from} → ${to}</div>
        <div class="rs-route">${t('sidebar.eta')}: ${fmtMin(eta)} (~${Math.ceil(remaining)}min)</div>
      `;
    } else {
      section.innerHTML = `
        <div class="rs-section-title">${t('sidebar.route')}</div>
        <div class="rs-route" style="color:var(--text-lo)">${t('sidebar.stationary')}</div>
      `;
    }
  }

  /** Bundle the runner + ripperdocs + callback into the shared module's
   *  options shape. Pure read; safe to call from render() and again
   *  from the wire pass. */
  private cyberwareOpts(): CyberwareUIOptions {
    return {
      runner: this.data!.runner,
      ripperdocs: this.data?.ripperdocs,
      cleanCash: this.data?.cleanCash,
      onInstallCyberware: this.data?.onInstallCyberware,
    };
  }
}
